import { useState } from 'react';
import { navigate, storeModeratorToken } from '../lib/router';

/**
 * Entry point on every device: the moderator creates a session here, the
 * authors and the projection join with the 6-digit PIN (D1, F-1/F-2).
 *
 * Playback (2026-09-09) is the one entry that needs no PIN: it reads a
 * finished round's export straight from the disk of the device it runs on.
 */
export function Home() {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pinValid = /^\d{6}$/.test(pin);

  const createSession = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/sessions', { method: 'POST' });
      if (!res.ok) throw new Error(`Server antwortet nicht (${res.status}).`);
      const data = (await res.json()) as { pin: string; moderatorToken: string };
      storeModeratorToken(data.pin, data.moderatorToken);
      navigate(`#/mod/${data.pin}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Session konnte nicht erstellt werden.');
      setBusy(false);
    }
  };

  return (
    <div className="home">
      <header className="masthead">
        <h1>Schreibarena</h1>
        <p className="masthead__meta">
          Zwei Autor:innen · eine Geschichte · eine Schachuhr.
          <br />
          Gelöscht wird nicht.
        </p>
      </header>

      <div className="home__grid">
        <section className="card">
          <span className="card__no">01</span>
          <h2>Moderation</h2>
          <p>Neue Session erstellen. Du bekommst einen PIN für die Autor:innen und die Projektion.</p>
          <button className="btn btn--primary" onClick={createSession} disabled={busy} data-testid="create-session">
            {busy ? 'Wird erstellt…' : 'Session erstellen'}
          </button>
          {error && <p className="form-error">{error}</p>}
        </section>

        <section className="card">
          <span className="card__no">02</span>
          <h2>Mitschreiben</h2>
          <p>Mit dem PIN der Moderation beitreten. Wer zuerst beitritt, ist Autor:in 1 und beginnt.</p>
          <div className="pin-row">
            <input
              className="input input--pin"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              aria-label="Session-PIN"
              data-testid="pin-input"
            />
            <button
              className="btn"
              disabled={!pinValid}
              onClick={() => navigate(`#/write/${pin}`)}
              data-testid="join-author"
            >
              Beitreten
            </button>
          </div>
        </section>

        <section className="card">
          <span className="card__no">03</span>
          <h2>Projektion</h2>
          <p>Nur-Lese-Ansicht für die große Leinwand — Text, beide Uhren, aktiver Joker.</p>
          <button
            className="btn"
            disabled={!pinValid}
            onClick={() => navigate(`#/beam/${pin}`)}
            data-testid="join-projection"
          >
            Projektion öffnen
          </button>
        </section>

        <section className="card card--wide card--play">
          <span className="card__no">04</span>
          <h2>Playback</h2>
          <p>
            Eine gespielte Runde noch einmal — der Export wird geladen und das Spiel läuft in Echtzeit ab, wie es
            gespielt wurde: Anschlag für Anschlag, beide Uhren, Joker und Strafen, auf derselben Leinwand. Oder als
            fertiger 9:16-Film fürs Netz, ohne Kamera und ohne Schnitt.
          </p>
          <div className="btn-row">
            <button className="btn" onClick={() => navigate('#/play')} data-testid="open-playback">
              Aufnahme abspielen
            </button>
            <button className="btn btn--ghost" onClick={() => navigate('#/clip')} data-testid="open-clip">
              Clip fürs Netz (9:16)
            </button>
          </div>
        </section>
      </div>

      <footer className="home__foot">
        <span>Osiander × Flügelmann</span>
        <span>Physische Tastatur erforderlich — Autokorrektur zählt nicht.</span>
      </footer>
    </div>
  );
}
